
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { artworksAPI, categoriesAPI } from '../services/api';
import '../styles/AdminDashboard.css';

// MULTISIZE TEST: This is a trivial change to force a redeploy and confirm the latest code is deployed.
console.log('MULTISIZE TEST: AdminDashboard.js loaded');

const AdminDashboard = () => {
        // Helper for description validation color
        const getDescriptionValidationColor = () => {
          const len = formData.description?.length || 0;
          if (len === 0) return '';
          if (len < 15) return 'desc-too-short';
          if (len <= 5000) return 'desc-valid';
          return 'desc-too-long';
        };
      // Validate a single field and update formErrors
      const validateField = (name, value) => {
        let error = '';
        if (name === 'title') {
          if (!value || value.trim().length < 2) error = 'Title must be at least 2 characters.';
        } else if (name === 'category') {
          if (!value || value.trim().length < 2) error = 'Category is required.';
        } else if (name === 'description') {
          if (!value || value.trim().length < 15) error = 'Description must be at least 15 characters.';
        } else if (name === 'price') {
          if (!value || isNaN(value) || Number(value) < 0) error = 'Price must be a valid number.';
        } else if (name === 'materials') {
          if (value && value.length > 255) error = 'Materials must be less than 255 characters.';
        }
        setFormErrors(prev => ({ ...prev, [name]: error }));
      };
    const [formErrors, setFormErrors] = useState({});
  const { logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [artworks, setArtworks] = useState([]);
  // Bulk Art Lookup state
  const [bulkIds, setBulkIds] = useState('');
  const [bulkResults, setBulkResults] = useState([]);
  const [showBulkResults, setShowBulkResults] = useState(false);
  const [hoveredImg, setHoveredImg] = useState(null);

  const handleBulkLookup = (e) => {
    e.preventDefault();
    const ids = bulkIds.split(',').map(id => id.trim()).filter(Boolean);
    if (ids.length === 0) {
      setBulkResults([]);
      setShowBulkResults(false);
      return;
    }
    // Find artworks matching any of the IDs
    const matches = artworks.filter(a => ids.includes(String(a.id)));
    setBulkResults(matches);
    setShowBulkResults(true);
  };

  const handleCloseBulkResults = () => {
    setShowBulkResults(false);
    setBulkResults([]);
    setBulkIds('');
  };
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    price: '',
    materials: '',
    featured: false,
    status: 'IN_STOCK',
    show_on_website: false,
    sizes: [{ size: '', price: '' }]
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  
  // Pricing settings state
  const [pricingSettings, setPricingSettings] = useState({
    fx_rate: 83,
    usd_multiplier: 1.75
  });
  const [calculatedUsd, setCalculatedUsd] = useState(0);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/ckk-secure-admin');
      return;
    }
    loadArtworks();
    loadPricingSettings();
    categoriesAPI.getAll()
      .then(data => {
        setCategories(data);
        setFormData(prev => ({ ...prev, category: prev.category || (data[0]?.id ?? '') }));
      })
      .catch(err => console.error('Failed to load categories:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Recalculate USD price whenever INR price or pricing settings change
  useEffect(() => {
    if (formData.price && pricingSettings.fx_rate && pricingSettings.usd_multiplier) {
      const usd = calculateUsdPrice(Number(formData.price), pricingSettings.fx_rate, pricingSettings.usd_multiplier);
      setCalculatedUsd(usd);
    } else {
      setCalculatedUsd(0);
    }
  }, [formData.price, pricingSettings]);

  const loadArtworks = async () => {
    try {
      // Add timestamp to force fresh fetch
      const data = await artworksAPI.getAll(`?_ts=${Date.now()}`);
      console.log('Loaded artworks:', data);
      setArtworks(data);
    } catch (error) {
      console.error('Failed to load artworks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPricingSettings = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/pricing-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPricingSettings({
          fx_rate: data.fx_rate,
          usd_multiplier: data.usd_multiplier
        });
      }
    } catch (error) {
      console.error('Error fetching pricing settings:', error);
    }
  };

  // Helper function to calculate USD price with psychological rounding
  const calculateUsdPrice = (inrPrice, fxRate, multiplier) => {
    if (!inrPrice || !fxRate || !multiplier) return 0;
    
    const baseUsd = (inrPrice / fxRate) * multiplier;
    
    // Psychological rounding logic (match backend)
    if (baseUsd < 10) {
      // Round to .99 (e.g., 7.99)
      return Math.ceil(baseUsd) - 0.01;
    } else if (baseUsd < 100) {
      // Round to .49 or .99 (e.g., 49.99, 59.49)
      const tens = Math.floor(baseUsd / 10) * 10;
      const remainder = baseUsd - tens;
      if (remainder <= 5) {
        return tens + 4.99;
      } else {
        return tens + 9.99;
      }
    } else {
      // Round to .99 (e.g., 149.99, 249.99)
      return Math.ceil(baseUsd / 10) * 10 - 0.01;
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle size/price pair changes
  const handleSizeChange = (index, field, value) => {
    setFormData(prev => {
      const sizes = prev.sizes.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      );
      return { ...prev, sizes };
    });
  };

  const addSize = () => {
    setFormData(prev => ({
      ...prev,
      sizes: [...prev.sizes, { size: '', price: '' }]
    }));
  };

  const removeSize = (index) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Frontend validation
    const errors = {};
    if (!formData.title || formData.title.trim().length < 2) {
      errors.title = 'Title must be at least 2 characters.';
    }
    if (!formData.category || formData.category.trim().length < 2) {
      errors.category = 'Category is required.';
    }
    if (!formData.description || formData.description.trim().length < 15) {
      errors.description = 'Description must be at least 15 characters.';
    }
    if (formData.price !== '' && formData.price != null) {
      if (isNaN(formData.price) || Number(formData.price) < 0) {
        errors.price = 'Price must be a valid positive number (or leave blank for "Price on request").';
      }
    }
    if (formData.materials && formData.materials.length > 255) {
      errors.materials = 'Materials must be less than 255 characters.';
    }
    if (!formData.sizes || !Array.isArray(formData.sizes) || formData.sizes.some(s => !s.size || !s.price)) {
      errors.sizes = 'All sizes and prices must be filled.';
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const data = new FormData();
    data.append('title', formData.title);
    data.append('category', formData.category);
    data.append('description', formData.description);
    data.append('price', formData.price ?? '');
    data.append('materials', formData.materials);
    data.append('featured', formData.featured);
    data.append('status', formData.status || 'IN_STOCK');
    data.append('show_on_website', String(Boolean(formData.show_on_website)));
    data.append('sizes', JSON.stringify(formData.sizes));
    if (imageFile) {
      data.append('image', imageFile);
    }
    try {
      if (editingArtwork) {
        await artworksAPI.update(editingArtwork.id, data);
      } else {
        await artworksAPI.create(data);
      }
      resetForm();
      loadArtworks();
    } catch (error) {
      console.error('Failed to save artwork:', error);
      alert('Failed to save artwork');
    }
  };

  const handleEdit = (artwork) => {
    console.log('Editing artwork:', artwork);
    setEditingArtwork(artwork);
    let sizes = [];
    if (Array.isArray(artwork.sizes) && artwork.sizes.length > 0) {
      sizes = artwork.sizes.map(sp => ({
        size: sp.size_label || '',
        price: sp.price || ''
      }));
    } else if (artwork.dimensions) {
      sizes = [{ size: artwork.dimensions, price: artwork.price || '' }];
    } else {
      sizes = [{ size: '', price: '' }];
    }
    setFormData({
      title: artwork.title,
      category: artwork.category,
      description: artwork.description,
      price: artwork.price_inr != null ? String(artwork.price_inr) : '',
      materials: artwork.materials,
      featured: artwork.featured,
      status: artwork.status || 'IN_STOCK',
      show_on_website: Boolean(artwork.show_on_website),
      sizes
    });
    setImagePreview(artwork.image || '');
    setShowForm(true);
    // Scroll to top when edit form opens
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this artwork?')) {
      try {
        await artworksAPI.delete(id);
        loadArtworks();
      } catch (error) {
        console.error('Failed to delete artwork:', error);
        alert('Failed to delete artwork');
      }
    }
  };

  const handleUpdateUsd = async (artwork) => {
    const current = artwork.price_usd ? Number(artwork.price_usd).toFixed(2) : '';
    const input = window.prompt(`Enter new USD price for "${artwork.title}" (current: $${current})`, current);
    if (input === null) return; // cancelled
    const value = parseFloat(input);
    if (isNaN(value) || value < 0) {
      alert('Please enter a valid non-negative number');
      return;
    }
    try {
      await artworksAPI.updatePrice(artwork.id, value);
      alert('USD price updated');
      loadArtworks();
    } catch (error) {
      console.error('Failed to update USD price:', error);
      alert('Failed to update USD price');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'dot-mandala',
      description: '',
      price: '',
      materials: '',
      featured: false,
      status: 'IN_STOCK',
      show_on_website: false,
      sizes: [{ size: '', price: '' }]
    });
    setImageFile(null);
    setImagePreview('');
    setEditingArtwork(null);
    setShowForm(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/ckk-secure-admin');
  };

  if (loading) {
    return <div className="admin-dashboard"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-actions">
          <button onClick={() => navigate('/ckk-secure-admin/inventory-sync')} className="btn-secondary">
            Inventory Sync
          </button>
          <button onClick={() => navigate('/ckk-secure-admin/review-queue')} className="btn-secondary">
            Review Queue
          </button>
          <button onClick={() => navigate('/ckk-secure-admin/order-requests')} className="btn-secondary">
            Order Requests
          </button>
          <button onClick={() => navigate('/ckk-secure-admin/shipments')} className="btn-secondary">
            Shipments
          </button>
          <button onClick={() => navigate('/ckk-secure-admin/physical-inventory')} className="btn-secondary">
            Physical Inventory
          </button>
          <button onClick={() => navigate('/ckk-secure-admin/categories')} className="btn-secondary">
            Manage Categories
          </button>
          <button onClick={() => navigate('/ckk-secure-admin/about')} className="btn-secondary">
            Edit About Page
          </button>
          <button onClick={() => navigate('/ckk-secure-admin/contact')} className="btn-secondary">
            Edit Contact Info
          </button>
          <button onClick={() => navigate('/ckk-secure-admin/settings')} className="btn-secondary">
            Site Settings
          </button>
          <button onClick={() => navigate('/')} className="btn-secondary">
            View Site
          </button>
          <button onClick={handleLogout} className="btn-logout">
            Logout
          </button>
        </div>
      </div>

      {/* Bulk Art Lookup Section */}
      <div className="bulk-art-lookup" style={{ marginBottom: '2rem', background: '#f9f9f9', padding: '1rem', borderRadius: '8px', position: 'relative' }}>
        <h2>Bulk Art Lookup (Paste Art IDs)</h2>
        <form onSubmit={handleBulkLookup} style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <textarea
            value={bulkIds}
            onChange={e => setBulkIds(e.target.value)}
            placeholder="Paste comma-separated art IDs here"
            rows={2}
            style={{ minWidth: '260px', flex: 2 }}
          />
          <button type="submit" className="btn-primary" style={{ flex: 0 }}>Search</button>
        </form>
        {showBulkResults && (
          <div className="bulk-results-modal" style={{
            position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.25)', zIndex: 3000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ background: '#fff', borderRadius: '10px', padding: '2rem', minWidth: '60vw', maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 32px rgba(0,0,0,0.18)', position: 'relative' }}>
              <button onClick={handleCloseBulkResults} style={{ position: 'absolute', top: 10, right: 18, fontSize: '2rem', background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}>&times;</button>
              <h3>Lookup Results</h3>
              <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: '1rem', fontSize: '1.05rem' }}>
                <thead>
                  <tr style={{ background: '#f2f2f2' }}>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>Image</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>Title</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>Category</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>Price</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>Size</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>Featured</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkResults.map(artwork => (
                    <tr key={artwork.id}>
                      <td style={{ textAlign: 'center', border: '1px solid #ddd', padding: '8px' }}>
                        {artwork.image ? (
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <img
                              src={artwork.image + (artwork.updatedAt ? `?t=${encodeURIComponent(artwork.updatedAt)}` : '')}
                              alt={artwork.title}
                              className="artwork-thumbnail"
                              style={{ userSelect: 'none', maxWidth: '60px', maxHeight: '60px', borderRadius: '6px', transition: 'box-shadow 0.2s' }}
                              onMouseEnter={() => setHoveredImg(artwork.image + (artwork.updatedAt ? `?t=${encodeURIComponent(artwork.updatedAt)}` : ''))}
                              onMouseLeave={() => setHoveredImg(null)}
                            />
                            {hoveredImg === (artwork.image + (artwork.updatedAt ? `?t=${encodeURIComponent(artwork.updatedAt)}` : '')) && (
                              <div style={{ position: 'absolute', left: '110%', top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: '#fff', border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 2px 12px rgba(0,0,0,0.18)', padding: '6px' }}>
                                <img src={hoveredImg} alt="Preview" style={{ maxWidth: '220px', maxHeight: '220px', borderRadius: '8px' }} />
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="artwork-thumbnail-placeholder">No image</span>
                        )}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{artwork.title}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{artwork.category}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>₹{artwork.price.toLocaleString()}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{Array.isArray(artwork.sizes) && artwork.sizes.length > 0 ? (artwork.sizes[0].size_label || artwork.sizes[0].size || '') : (artwork.dimensions || '')}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{artwork.featured ? '⭐ Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <div className="dashboard-content">
        <div className="dashboard-toolbar">
          <h2>Manage Artworks</h2>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary"
          >
            {showForm ? 'Cancel' : '+ Add New Artwork'}
          </button>
        </div>

        {showForm && (
          <div className="artwork-form-container">
            <h3>{editingArtwork ? 'Edit Artwork' : 'Add New Artwork'}</h3>
            <form onSubmit={handleSubmit} className="artwork-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="title">Title *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    onBlur={e => validateField('title', e.target.value)}
                    required
                  />
                  {formErrors.title && <div className="form-error">{formErrors.title}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    onBlur={e => validateField('category', e.target.value)}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {formErrors.category && <div className="form-error">{formErrors.category}</div>}
                </div>
              </div>


              <div className="form-group">
                <label htmlFor="description">Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  onBlur={e => validateField('description', e.target.value)}
                  rows="3"
                  required
                  className={getDescriptionValidationColor()}
                ></textarea>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className={getDescriptionValidationColor()}>
                    {formData.description.length} / 5000
                  </span>
                  {formData.description.length > 0 && formData.description.length < 15 && (
                    <span className="form-error">Description must be at least 15 characters.</span>
                  )}
                  {formData.description.length > 5000 && (
                    <span className="form-error">Description must be less than 5000 characters.</span>
                  )}
                </div>
              </div>
              {/* Main price field for backend compatibility */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="price">Master Price (INR)</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price || ''}
                    onChange={handleInputChange}
                    onBlur={e => validateField('price', e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="Leave blank for Price on request"
                  />
                  <small>Base price in Indian Rupees. Leave blank for "Price on request". USD is auto-calculated.</small>
                  {formErrors.price && <div className="form-error">{formErrors.price}</div>}
                </div>
              </div>

              {/* Auto-calculated USD price display */}
              {formData.price && (
                <div className="form-row">
                  <div className="form-group calculated-usd-display">
                    <label>Calculated USD Price (Read-only)</label>
                    <div className="usd-price-box">
                      <span className="usd-amount">${calculatedUsd.toFixed(2)}</span>
                      <small className="usd-formula">
                        Formula: (₹{Number(formData.price).toLocaleString('en-IN')} ÷ {pricingSettings.fx_rate}) × {pricingSettings.usd_multiplier} = ${calculatedUsd.toFixed(2)}
                      </small>
                      <small className="usd-note">
                        Using FX Rate: {pricingSettings.fx_rate} INR/USD | Multiplier: {pricingSettings.usd_multiplier}x
                      </small>
                    </div>
                  </div>
                </div>
              )}

              {/* Status and visibility controls */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status || 'IN_STOCK'}
                    onChange={handleInputChange}
                  >
                    <option value="IN_STOCK">In Stock</option>
                    <option value="MADE_TO_ORDER">Made to Order</option>
                    <option value="NEEDS_REVIEW">Needs Review</option>
                    <option value="OUT_OF_STOCK">Out of Stock</option>
                    <option value="SOLD">Sold</option>
                    <option value="ARCHIVED">Archived</option>
                  </select>
                  <small>Status alone does not make artwork visible — also enable "Show on Website".</small>
                </div>
                <div className="form-group">
                  <label>Show on Website</label>
                  <label className="dash-checkbox-label">
                    <input
                      type="checkbox"
                      name="show_on_website"
                      checked={Boolean(formData.show_on_website)}
                      onChange={e => setFormData(prev => ({ ...prev, show_on_website: e.target.checked }))}
                    />
                    <span>Visible to public</span>
                  </label>
                  <small>Artwork appears on the public site only when In Stock or Made to Order AND this is checked.</small>
                </div>
              </div>

              {/* SKU — system-generated, read-only */}
              <div className="form-row">
                <div className="form-group">
                  <label>SKU (system-generated)</label>
                  {editingArtwork?.sku ? (
                    <div className="usd-price-box">
                      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{editingArtwork.sku}</span>
                      <small>SKU is assigned automatically and cannot be changed.</small>
                    </div>
                  ) : (
                    <div className="usd-price-box">
                      <span style={{ color: '#888' }}>Not yet assigned</span>
                      <small>SKU will be auto-generated when artwork is saved as In Stock or Made to Order with "Show on Website" enabled.</small>
                    </div>
                  )}
                </div>
              </div>

              {/* Multiple sizes/prices UI */}
              <div className="form-group">
                <label>Sizes & Prices *</label>
                {console.log('Rendering sizes input:', formData.sizes)}
                {formData.sizes.map((sp, idx) => (
                  <div className="size-price-row" key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      placeholder={'Size (e.g., 12" x 12")'}
                      value={sp.size}
                      onChange={e => handleSizeChange(idx, 'size', e.target.value)}
                      required
                      style={{ flex: 2 }}
                    />
                    <input
                      type="number"
                      placeholder="Price (₹)"
                      value={sp.price}
                      onChange={e => handleSizeChange(idx, 'price', e.target.value)}
                      min="0"
                      step="0.01"
                      required
                      style={{ flex: 1 }}
                    />
                    {formData.sizes.length > 1 && (
                      <button type="button" className="btn-remove-size" onClick={() => removeSize(idx)} style={{ flex: 0 }}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn-add-size" onClick={addSize} style={{ marginTop: '8px' }}>
                  + Add Size/Price
                </button>
                {formErrors.sizes && <div className="form-error">{formErrors.sizes}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="materials">Materials *</label>
                <input
                  type="text"
                  id="materials"
                  name="materials"
                  value={formData.materials}
                  onChange={handleInputChange}
                  onBlur={e => validateField('materials', e.target.value)}
                  placeholder="e.g., Acrylic on canvas"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="image">Image {editingArtwork ? '' : '*'}</label>
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  required={!editingArtwork}
                />
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                  />
                  <span>Featured Artwork</span>
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingArtwork ? 'Update Artwork' : 'Create Artwork'}
                </button>
                <button type="button" onClick={resetForm} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="artworks-table">
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Category</th>
                <th>Price (INR)</th>
                <th>Price (USD)</th>
                <th>Size</th>
                <th>Featured</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {artworks.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    No artworks yet. Add your first artwork!
                  </td>
                </tr>
              ) : (
                artworks.map(artwork => (
                  <tr key={artwork.id}>
                    <td>
                      {artwork.image ? (
                        <img
                          src={artwork.image + (artwork.updatedAt ? `?t=${encodeURIComponent(artwork.updatedAt)}` : '')}
                          alt={artwork.title}
                          className="artwork-thumbnail"
                          onContextMenu={e => e.preventDefault()}
                          draggable={false}
                          style={{ userSelect: 'none', pointerEvents: 'none' }}
                        />
                      ) : (
                        <span className="artwork-thumbnail-placeholder">No image</span>
                      )}
                    </td>
                    <td>{artwork.title}</td>
                    <td>{artwork.category}</td>
                    <td>₹{(artwork.price_inr || artwork.price || 0).toLocaleString()}</td>
                    <td>${artwork.price_usd ? Number(artwork.price_usd).toFixed(2) : '—'}</td>
                    <td>{
                      Array.isArray(artwork.sizes) && artwork.sizes.length > 0
                        ? (artwork.sizes[0].size_label || artwork.sizes[0].size || '')
                        : (artwork.dimensions || '')
                    }</td>
                    <td>{artwork.featured ? '⭐ Yes' : 'No'}</td>
                    <td className="actions-cell">
                      <button
                        onClick={() => handleEdit(artwork)}
                        className="btn-edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleUpdateUsd(artwork)}
                        className="btn-adjust"
                        style={{ marginLeft: '6px' }}
                      >
                        Adjust USD
                      </button>
                      <button
                        onClick={() => handleDelete(artwork.id)}
                        className="btn-delete"
                        style={{ marginLeft: '6px' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
